import React from 'react';
import {
  reduxForm,
  InjectedFormProps,
  change,
  getFormSyncErrors,
  getFormValues,
  reset,
} from 'redux-form';
import { DialogContent, DialogActions, Grid, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { buildQaId } from 'utils/build-qa-id';
import { useDispatch, connect } from 'react-redux';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { validateEmail } from 'components/form/validation/validation';
import {
  FORM_NAME,
  MODAL_QA_ID,
  MISSING_INFO_TITLE,
  PHARMACY_INFORMATION_MAPPING,
  HEALTH_SYSTEM_INFORMATION_MAPPING,
  HsiDynamicPairFieldsEnum,
  PHARMACY_INFORMATION,
  HEALTH_SYSTEM_INFORMATION,
  LOGINS_ACCOUNTS,
  LaDynamicPairFieldsEnum,
  LOGINS_ACCOUNTS_MAPPING,
} from '../constants';
import { IFormFixedFields } from './types';
import { cpStyle } from '../index.styles';
import { PharmacyInformation } from './sub-forms/pharmacy-information';
import { LoginsAccounts } from './sub-forms/logins-accounts';
import { HealthSystemInformation } from './sub-forms/health-system-information';
import { IResult, IFields } from '../table/types';

export interface IProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  onCancel: () => void;
  allCustomers: { label: string; value: number }[];
  selectedParentRow: IResult | null;
  customerId: number;
}

interface IStateProps {
  missingValues: { [key: string]: any };
  formValues: any;
}

type ICustomerProfileFormProps = IProps & IStateProps & InjectedFormProps<IFormFixedFields, IProps>;

const CustomerProfileInnerForm = (props: ICustomerProfileFormProps) => {
  const useStyles = makeStyles(cpStyle);
  const classes = useStyles();
  const qaIdBuilder = buildQaId(MODAL_QA_ID);
  const dispatch = useDispatch();

  const dynamicPoolList: any = React.useRef([]);
  const dynamicSureScriptList: any = React.useRef([]);
  const dynamicPrinterList: any = React.useRef([]);
  const dynamicSiteList: any = React.useRef([]);
  const dynamicFoundationList: any = React.useRef([]);
  const dynamicSupplyOrderList: any = React.useRef([]);

  const siteIdMapper: any = React.useRef([]);
  const foundationsIdMapper: any = React.useRef([]);
  const supplyOrderIdMapper: any = React.useRef([]);

  const {
    onSuccess,
    onCancel,
    onError,
    handleSubmit,
    allCustomers,
    selectedParentRow,
    customerId,
    missingValues,
  } = props;

  const [sharedEmailErrorMessage, setSharedEmailErrorMessage] = React.useState<string>('');
  const [distributionListEmailErrorMessage, setDistributionListEmailErrorMessage] =
    React.useState<string>('');

  const [missingFields, setMissingFields] = React.useState<
    { section: string; fieldName: string }[]
  >([]);

  const updateDynamicPoolList = (poolList: { id: number }[]) => {
    dynamicPoolList.current = poolList;
  };

  const updateDynamicSureScriptList = (sureScriptList: { id: number }[]) => {
    dynamicSureScriptList.current = sureScriptList;
  };

  const updateDynamicPrinterList = (printerList: { id: number }[]) => {
    dynamicPrinterList.current = printerList;
  };

  const updateDynamicSiteList = (siteList: { id: number }[]) => {
    dynamicSiteList.current = siteList;
  };

  const updateDynamicFoundationList = (foundationList: { id: number }[]) => {
    dynamicFoundationList.current = foundationList;
  };

  const updateDynamicSupplyOrderList = (supplyOrderList: { id: number }[]) => {
    dynamicSupplyOrderList.current = supplyOrderList;
  };
  const updateSiteIdMapper = (siteMapper: { dbId: number; counter: number }[]) => {
    siteIdMapper.current = siteIdMapper.current.concat(siteMapper);
  };
  const updateFoundationsIdMapper = (foundationsMapper: { dbId: number; counter: number }[]) => {
    foundationsIdMapper.current = foundationsIdMapper.current.concat(foundationsMapper);
  };

  const updateSupplyOrderIdMapper = (supplyOrderMapper: { dbId: number; counter: number }[]) => {
    supplyOrderIdMapper.current = supplyOrderIdMapper.current.concat(supplyOrderMapper);
  };

  const checkEmailFormat = (email: string | null, filedName: string) => {
    if (email) {
      const tempVal = validateEmail(email);
      if (tempVal) {
        if (filedName === 'hsi_shared_email' && sharedEmailErrorMessage === '') {
          setSharedEmailErrorMessage(tempVal);
        }
        if (
          filedName === 'hsi_distribution_list_email' &&
          distributionListEmailErrorMessage === ''
        ) {
          setDistributionListEmailErrorMessage(tempVal);
        }
      } else {
        if (filedName === 'hsi_shared_email' && sharedEmailErrorMessage !== '') {
          setSharedEmailErrorMessage('');
        }
        if (
          filedName === 'hsi_distribution_list_email' &&
          distributionListEmailErrorMessage !== ''
        ) {
          setDistributionListEmailErrorMessage('');
        }
      }
    } else {
      if (filedName === 'hsi_shared_email' && sharedEmailErrorMessage !== '') {
        setSharedEmailErrorMessage('');
      }
      if (filedName === 'hsi_distribution_list_email' && distributionListEmailErrorMessage !== '') {
        setDistributionListEmailErrorMessage('');
      }
    }
  };

  const getPharmacyInformationData = (formData: { [key: string]: any }) => {
    const mappedObject = PHARMACY_INFORMATION_MAPPING.reduce<Record<string, string>>((acc, val) => {
      acc[val.nickName] = formData[val.fieldName] ?? null;
      return acc;
    }, {});
    return mappedObject;
  };
  const getHealthSystemInformationData = (formData: { [key: string]: any }) => {
    const tempDynamicFields = (Object.values(HsiDynamicPairFieldsEnum) || []) as string[];
    const mappedObject = HEALTH_SYSTEM_INFORMATION_MAPPING.reduce<Record<string, string>>(
      (acc, val) => {
        if (tempDynamicFields.includes(val.fieldName)) {
          if (([HsiDynamicPairFieldsEnum.POOL_NAMES] as string[]).includes(val.fieldName)) {
            if (dynamicPoolList.current?.length) {
              const maxId = Math.max(...dynamicPoolList.current?.map((item: any) => item.id));
              const tempValues = [];
              const tempClinicValues = [];
              for (let id = 0; id <= maxId; id += 1) {
                const tempVal = formData[`${val.fieldName}_${id}`];
                if (tempVal) {
                  tempValues.push(tempVal.trim());
                }
                const tempClinicVal =
                  formData[`${HsiDynamicPairFieldsEnum.POOL_CLINIC_REFERENCE}_${id}`];
                if (tempClinicVal) {
                  tempClinicValues.push(tempClinicVal.trim());
                }
              }
              if (tempValues.length) {
                acc[val.nickName] = tempValues.join(';');
              }
              if (tempClinicValues.length) {
                const tempObj = HEALTH_SYSTEM_INFORMATION_MAPPING.find(
                  t => t.fieldName === HsiDynamicPairFieldsEnum.POOL_CLINIC_REFERENCE,
                );
                if (tempObj) {
                  acc[tempObj.nickName] = tempClinicValues.join(';');
                }
              }
            } else {
              const tempVal = formData[`${val.fieldName}_0`];
              if (tempVal) {
                acc[val.nickName] = tempVal;
              }
              const tempRefVal = formData[`${HsiDynamicPairFieldsEnum.POOL_CLINIC_REFERENCE}_0`];
              if (tempRefVal) {
                const tempObj = HEALTH_SYSTEM_INFORMATION_MAPPING.find(
                  t => t.fieldName === HsiDynamicPairFieldsEnum.POOL_CLINIC_REFERENCE,
                );
                if (tempObj) {
                  acc[tempObj.nickName] = tempRefVal;
                }
              }
            }
          }
          if (([HsiDynamicPairFieldsEnum.SURESCRIPTS_NAME] as string[]).includes(val.fieldName)) {
            if (dynamicSureScriptList.current?.length) {
              const maxId = Math.max(...dynamicSureScriptList.current?.map((item: any) => item.id));
              const tempValues = [];
              const tempClinicValues = [];
              for (let id = 0; id <= maxId; id += 1) {
                const tempVal = formData[`${val.fieldName}_${id}`];
                if (tempVal) {
                  tempValues.push(tempVal.trim());
                }
                const tempClinicVal =
                  formData[`${HsiDynamicPairFieldsEnum.SURESCRIPTS_CLINIC_REFERENCE}_${id}`];
                if (tempClinicVal) {
                  tempClinicValues.push(tempClinicVal.trim());
                }
              }
              if (tempValues.length) {
                acc[val.nickName] = tempValues.join(';');
              }
              if (tempClinicValues.length) {
                const tempObj = HEALTH_SYSTEM_INFORMATION_MAPPING.find(
                  t => t.fieldName === HsiDynamicPairFieldsEnum.SURESCRIPTS_CLINIC_REFERENCE,
                );
                if (tempObj) {
                  acc[tempObj.nickName] = tempClinicValues.join(';');
                }
              }
            } else {
              const tempVal = formData[`${val.fieldName}_0`];
              if (tempVal) {
                acc[val.nickName] = tempVal;
              }
              const tempRefVal =
                formData[`${HsiDynamicPairFieldsEnum.SURESCRIPTS_CLINIC_REFERENCE}_0`];
              if (tempRefVal) {
                const tempObj = HEALTH_SYSTEM_INFORMATION_MAPPING.find(
                  t => t.fieldName === HsiDynamicPairFieldsEnum.SURESCRIPTS_CLINIC_REFERENCE,
                );
                if (tempObj) {
                  acc[tempObj.nickName] = tempRefVal;
                }
              }
            }
          }
          if (([HsiDynamicPairFieldsEnum.PRINTER] as string[]).includes(val.fieldName)) {
            if (dynamicPrinterList.current?.length) {
              const maxId = Math.max(...dynamicPrinterList.current?.map((item: any) => item.id));
              const tempValues = [];
              const tempClinicValues = [];
              for (let id = 0; id <= maxId; id += 1) {
                const tempVal = formData[`${val.fieldName}_${id}`];
                if (tempVal) {
                  tempValues.push(tempVal.trim());
                }
                const tempClinicVal =
                  formData[`${HsiDynamicPairFieldsEnum.PRINTER_CLINIC_REFERENCE}_${id}`];
                if (tempClinicVal) {
                  tempClinicValues.push(tempClinicVal.trim());
                }
              }
              if (tempValues.length) {
                acc[val.nickName] = tempValues.join(';');
              }
              if (tempClinicValues.length) {
                const tempObj = HEALTH_SYSTEM_INFORMATION_MAPPING.find(
                  t => t.fieldName === HsiDynamicPairFieldsEnum.PRINTER_CLINIC_REFERENCE,
                );
                if (tempObj) {
                  acc[tempObj.nickName] = tempClinicValues.join(';');
                }
              }
            } else {
              const tempVal = formData[`${val.fieldName}_0`];
              if (tempVal) {
                acc[val.nickName] = tempVal;
              }
              const tempRefVal = formData[`${HsiDynamicPairFieldsEnum.PRINTER_CLINIC_REFERENCE}_0`];
              if (tempRefVal) {
                const tempObj = HEALTH_SYSTEM_INFORMATION_MAPPING.find(
                  t => t.fieldName === HsiDynamicPairFieldsEnum.PRINTER_CLINIC_REFERENCE,
                );
                if (tempObj) {
                  acc[tempObj.nickName] = tempRefVal;
                }
              }
            }
          }
          return acc;
        }

        acc[val.nickName] = formData[val.fieldName] ?? null;
        return acc;
      },
      {},
    );
    return mappedObject;
  };

  const getLoginsAccountsData = (formData: { [key: string]: any }) => {
    const tempDynamicFields = (Object.values(LaDynamicPairFieldsEnum) || []) as string[];
    const mappedObject = LOGINS_ACCOUNTS_MAPPING.reduce<Record<string, any>>((acc, val) => {
      if (tempDynamicFields.includes(val.fieldName)) {
        if (([LaDynamicPairFieldsEnum.SITE_USERNAME] as string[]).includes(val.fieldName)) {
          if (dynamicSiteList.current.length) {
            const maxId = Math.max(...dynamicSiteList.current?.map((item: any) => item.id));
            const tempValues = [];
            for (let id = 0; id <= maxId; id += 1) {
              const selectedSiteValue = formData[`${LaDynamicPairFieldsEnum.SITE}_${id}`];
              const usernameValue =
                formData[`${LaDynamicPairFieldsEnum.SITE_USERNAME}_${id}`]?.trim();
              const passValue = formData[`${LaDynamicPairFieldsEnum.SITE_PASSWORD}_${id}`]?.trim();
              if (selectedSiteValue && passValue) {
                if (!acc.SITE) {
                  acc.SITE = [];
                }
                const tempSiteData = {
                  [val.nickName.replace(/\?\?\?/, `${selectedSiteValue}`)]: `${usernameValue}`,
                  ['site_???_password'.replace(/\?\?\?/, `${selectedSiteValue}`)]: `${passValue}`,
                  id: siteIdMapper.current.find((t: any) => t.counter === id)?.dbId ?? null,
                };
                acc.SITE.push(tempSiteData);
              }
            }
          } else {
            const selectedSiteValue = formData[`${LaDynamicPairFieldsEnum.SITE}_0`];
            const usernameValue = formData[`${LaDynamicPairFieldsEnum.SITE_USERNAME}_0`]?.trim();
            const passValue = formData[`${LaDynamicPairFieldsEnum.SITE_PASSWORD}_0`]?.trim();
            if (selectedSiteValue && passValue) {
              if (!acc.SITE) {
                acc.SITE = [];
              }
              const tempSiteData = {
                [val.nickName.replace(/\?\?\?/, `${selectedSiteValue}`)]: `${usernameValue}`,
                ['site_???_password'.replace(/\?\?\?/, `${selectedSiteValue}`)]: `${passValue}`,
                id: siteIdMapper.current.find((t: any) => t.counter === 0)?.dbId ?? null,
              };
              acc.SITE.push(tempSiteData);
            }
          }
        }
        if (([LaDynamicPairFieldsEnum.FOUNDATION_USERNAME] as string[]).includes(val.fieldName)) {
          if (dynamicFoundationList.current.length) {
            const maxId = Math.max(...dynamicFoundationList.current?.map((item: any) => item.id));
            const tempValues = [];
            for (let id = 0; id <= maxId; id += 1) {
              const selectedFoundationsValue =
                formData[`${LaDynamicPairFieldsEnum.FOUNDATION}_${id}`];
              const usernameValue =
                formData[`${LaDynamicPairFieldsEnum.FOUNDATION_USERNAME}_${id}`]?.trim();
              const passValue =
                formData[`${LaDynamicPairFieldsEnum.FOUNDATION_PASSWORD}_${id}`]?.trim();
              if (selectedFoundationsValue && passValue) {
                if (!acc.FOUNDATIONS) {
                  acc.FOUNDATIONS = [];
                }
                const tempFoundationsData = {
                  [val.nickName.replace(
                    /\?\?\?/,
                    `${selectedFoundationsValue}`,
                  )]: `${usernameValue}`,
                  ['fo_???_password'.replace(
                    /\?\?\?/,
                    `${selectedFoundationsValue}`,
                  )]: `${passValue}`,
                  id: foundationsIdMapper.current.find((t: any) => t.counter === id)?.dbId ?? null,
                };
                acc.FOUNDATIONS.push(tempFoundationsData);
              }
            }
          } else {
            const selectedFoundationValue = formData[`${LaDynamicPairFieldsEnum.FOUNDATION}_0`];
            const usernameValue =
              formData[`${LaDynamicPairFieldsEnum.FOUNDATION_USERNAME}_0`]?.trim();
            const passValue = formData[`${LaDynamicPairFieldsEnum.FOUNDATION_PASSWORD}_0`]?.trim();
            if (selectedFoundationValue && passValue) {
              if (!acc.FOUNDATIONS) {
                acc.FOUNDATIONS = [];
              }
              const tempFoundationsData = {
                [val.nickName.replace(/\?\?\?/, `${selectedFoundationValue}`)]: `${usernameValue}`,
                ['fo_???_password'.replace(/\?\?\?/, `${selectedFoundationValue}`)]: `${passValue}`,
                id: foundationsIdMapper.current.find((t: any) => t.counter === 0)?.dbId ?? null,
              };
              acc.FOUNDATIONS.push(tempFoundationsData);
            }
          }
        }
        if (([LaDynamicPairFieldsEnum.SUPPLY_ORDER_USERNAME] as string[]).includes(val.fieldName)) {
          if (dynamicSupplyOrderList.current.length) {
            const maxId = Math.max(...dynamicSupplyOrderList.current?.map((item: any) => item.id));
            const tempValues = [];
            for (let id = 0; id <= maxId; id += 1) {
              const selectedSupplyOrderValue =
                formData[`${LaDynamicPairFieldsEnum.SUPPLY_ORDER}_${id}`];
              const usernameValue =
                formData[`${LaDynamicPairFieldsEnum.SUPPLY_ORDER_USERNAME}_${id}`]?.trim();
              const passValue =
                formData[`${LaDynamicPairFieldsEnum.SUPPLY_ORDER_PASSWORD}_${id}`]?.trim();
              if (selectedSupplyOrderValue && passValue) {
                if (!acc.SUPPLY_ORDER) {
                  acc.SUPPLY_ORDER = [];
                }
                const tempSupplyOrderData = {
                  [val.nickName.replace(
                    /\?\?\?/,
                    `${selectedSupplyOrderValue}`,
                  )]: `${usernameValue}`,
                  ['so_???_password'.replace(
                    /\?\?\?/,
                    `${selectedSupplyOrderValue}`,
                  )]: `${passValue}`,
                  id: supplyOrderIdMapper.current.find((t: any) => t.counter === id)?.dbId ?? null,
                };
                acc.SUPPLY_ORDER.push(tempSupplyOrderData);
              }
            }
          } else {
            const selectedSupplyOrderValue = formData[`${LaDynamicPairFieldsEnum.SUPPLY_ORDER}_0`];
            const usernameValue =
              formData[`${LaDynamicPairFieldsEnum.SUPPLY_ORDER_USERNAME}_0`]?.trim();
            const passValue =
              formData[`${LaDynamicPairFieldsEnum.SUPPLY_ORDER_PASSWORD}_0`]?.trim();
            if (selectedSupplyOrderValue && passValue) {
              if (!acc.SUPPLY_ORDER) {
                acc.SUPPLY_ORDER = [];
              }
              const tempSupplyOrderData = {
                [val.nickName.replace(/\?\?\?/, `${selectedSupplyOrderValue}`)]: `${usernameValue}`,
                ['so_???_password'.replace(
                  /\?\?\?/,
                  `${selectedSupplyOrderValue}`,
                )]: `${passValue}`,
                id: supplyOrderIdMapper.current.find((t: any) => t.counter === 0)?.dbId ?? null,
              };
              acc.SUPPLY_ORDER.push(tempSupplyOrderData);
            }
          }
        }
      }
      return acc;
    }, {});
    return mappedObject;
  };

  const renderMultiline = (message: string) => (
    <>
      {message.split('\n').map((line, idx) => (
        <React.Fragment key={idx}>
          {line}
          <br />
        </React.Fragment>
      ))}
    </>
  );

  const saveInfo = async (): Promise<void> => {
    if (props.formValues && Object.keys(props.formValues).length && selectedParentRow?.id) {
      const currentPharmacyInformationData = getPharmacyInformationData(props.formValues);
      const currentHealthSystemInformationData = getHealthSystemInformationData(props.formValues);
      const currentLoginsAccountsData = getLoginsAccountsData(props.formValues);
      const payload = {
        pharmacyId: selectedParentRow.id,
        pharmacyInformation: {
          id: selectedParentRow.children.find(t => t.title === PHARMACY_INFORMATION)?.id ?? null,
          ...currentPharmacyInformationData,
        },
        healthSystemInformation: {
          id:
            selectedParentRow.children.find(t => t.title === HEALTH_SYSTEM_INFORMATION)?.id ?? null,
          ...currentHealthSystemInformationData,
        },
        loginsAccounts: currentLoginsAccountsData,
      };
      const result = await ApplicationManagerClient.editCustomerProfilePharmacy(payload);
      if (result?.data?.success) {
        dispatch(notifySuccess('Saved'));
      } else if (!result?.data?.success && result?.data?.result) {
        dispatch(notifyError(renderMultiline(`${result.data.result}`)));
      }
    }
  };

  const onSubmit = async (formValues: any): Promise<void> => {
    if (formValues && Object.keys(formValues).length && selectedParentRow?.id) {
      const currentPharmacyInformationData = getPharmacyInformationData(formValues);
      const currentHealthSystemInformationData = getHealthSystemInformationData(formValues);
      const currentLoginsAccountsData = getLoginsAccountsData(props.formValues);
      const payload = {
        pharmacyId: selectedParentRow.id,
        pharmacyInformation: {
          id: selectedParentRow.children.find(t => t.title === PHARMACY_INFORMATION)?.id ?? null,
          ...currentPharmacyInformationData,
        },
        healthSystemInformation: {
          id:
            selectedParentRow.children.find(t => t.title === HEALTH_SYSTEM_INFORMATION)?.id ?? null,
          ...currentHealthSystemInformationData,
        },
        loginsAccounts: currentLoginsAccountsData,
      };
      const result = await ApplicationManagerClient.editCustomerProfilePharmacy(payload);
      if (result?.data?.success) {
        dispatch(notifySuccess('Saved'));
        dispatch(reset(FORM_NAME));
        onSuccess();
        onCancel();
      } else if (!result?.data?.success && result?.data?.result) {
        dispatch(notifyError(renderMultiline(`${result.data.result}`)));
      }
    }
  };

  const getFieldsInformation = (infoType: string): IFields[] => {
    const fieldsInfo =
      selectedParentRow?.children.find(t => t.title === infoType)?.fields ?? undefined;
    if (fieldsInfo) {
      return fieldsInfo;
    }
    return [];
  };

  const setFieldValue = (field: string, value: any) => dispatch(change(FORM_NAME, field, value));

  React.useEffect(() => {
    if (missingValues && Object.keys(missingValues).length) {
      const missingRequiredFields = Object.keys(missingValues).filter(field => {
        return missingValues[field] === 'Required' || missingValues[field]?.includes('required');
      });
      const tempMissingFields: { section: string; fieldName: string }[] = [];
      missingRequiredFields.map(requiredField => {
        const listToCheck = selectedParentRow?.children;
        if (listToCheck && listToCheck.length) {
          for (const section of listToCheck) {
            const found = (section.fields || []).find(
              t => t.fieldName && t.fieldName === requiredField,
            );
            if (found) {
              tempMissingFields.push({
                section: section.title,
                fieldName: found.title,
              });
            }
          }
        }
      });
      setMissingFields(tempMissingFields);
    } else if (missingFields.length) {
      setMissingFields([]);
    }
  }, [missingValues]);

  return (
    <>
      <DialogContent>
        <form data-qa-id={qaIdBuilder('form')} className={classes.customerProfileForm}>
          <Grid container spacing={1}>
            <HealthSystemInformation
              formTitle={HEALTH_SYSTEM_INFORMATION}
              fieldsInfo={getFieldsInformation(HEALTH_SYSTEM_INFORMATION)}
              setFieldValue={setFieldValue}
              updateDynamicPoolList={updateDynamicPoolList}
              updateDynamicSureScriptList={updateDynamicSureScriptList}
              updateDynamicPrinterList={updateDynamicPrinterList}
              sharedEmailErrorMessage={sharedEmailErrorMessage}
              distributionListEmailErrorMessage={distributionListEmailErrorMessage}
              checkEmailFormat={checkEmailFormat}
            />
            <PharmacyInformation
              formTitle={PHARMACY_INFORMATION}
              allCustomers={allCustomers}
              customerId={customerId}
              fieldsInfo={getFieldsInformation(PHARMACY_INFORMATION)}
              setFieldValue={setFieldValue}
            />
            <LoginsAccounts
              formTitle={LOGINS_ACCOUNTS}
              fieldsInfo={getFieldsInformation(LOGINS_ACCOUNTS)}
              setFieldValue={setFieldValue}
              updateDynamicSiteList={updateDynamicSiteList}
              updateDynamicFoundationList={updateDynamicFoundationList}
              updateDynamicSupplyOrderList={updateDynamicSupplyOrderList}
              updateSiteIdMapper={updateSiteIdMapper}
              updateFoundationsIdMapper={updateFoundationsIdMapper}
              updateSupplyOrderIdMapper={updateSupplyOrderIdMapper}
            />
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actionsWrp}>
        <Grid container>
          {missingFields.length > 0 && (
            /* eslint-disable react/jsx-closing-tag-location */
            <Grid
              container
              spacing={2}
              justifyContent="flex-end"
              className={classes.missingInfoContainer}
            >
              <Grid item xs={5} className={classes.missingInfoBox}>
                <span className={classes.missingInfoBoxTitle}>{`${MISSING_INFO_TITLE}`}</span>
                {missingFields
                  .sort((a, b) => (a.section > b.section ? 1 : -1))
                  .map(t => (
                    <Grid>
                      <span
                        className={classes.missingInfoRow}
                      >{`${t.section} - ${t.fieldName} *`}</span>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
            /* eslint-enable react/jsx-closing-tag-location */
          )}
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <Button onClick={onCancel}>Cancel</Button>
            </Grid>
            <Grid item>
              <Button color="primary" variant="contained" onClick={handleSubmit(onSubmit)}>
                Save and submit
              </Button>
            </Grid>
            <Grid item>
              <Button color="primary" variant="contained" onClick={() => saveInfo()}>
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

function mapStateToProps(state: any, ownProps: IProps) {
  const errors = getFormSyncErrors(FORM_NAME)(state);
  return {
    missingValues: errors,
    formValues: getFormValues(FORM_NAME)(state),
  };
}

const ConnectedCustomerProfileForm = connect(mapStateToProps)(CustomerProfileInnerForm);

export const CustomerProfileModalInnerForm = reduxForm<IFormFixedFields, IProps>({
  form: FORM_NAME,
})(ConnectedCustomerProfileForm);
